import { QueryParams, useListQueryParams } from '@/composables/helpers'
import { queryKeys } from '@/lib/query-keys/v1'
import { logSchema, logsSchema } from '@/lib/schema/v1/log'
import {
    paginatedTicketDepartmentsSchema,
    ticketDepartmentSchema,
    TicketDepartmentUpdateForm,
} from '@/lib/schema/v1/ticket-department'
import { Filter, Sort } from '@/lib/types/v1'
import { Ticket, TicketPagination } from '@/types'
import httpClient from '@/utils/httpClient'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, ref, Ref, watchEffect } from 'vue'
import { toast } from 'vue-sonner'

export type TicketDepartmentListParams = {
    page?: Ref<number | undefined>
    limit?: Ref<number | undefined>
    sort?: Ref<Sort[] | undefined>
    filter?: Ref<Filter[] | undefined>
    q?: Ref<string | undefined>
    view?: Ref<string | undefined>
    enabled?: Ref<boolean | undefined>
    staleTime?: Ref<number | undefined>
}

export type FindTicketDepartmentParams = {
    uuid: Ref<string>
    enabled: Ref<boolean>
    staleTime?: Ref<number>
}

const QueryKeys = {
    TicketDepartments: 'ticket-departments',
    Paginated: 'paginated',
}

export const useFindTicketDepartmentQuery = (params: FindTicketDepartmentParams) => {
    const staleTime = ref(1000 * 60 * 5)
    const key = computed(() => ['ticket-departments', `uuid:${params.uuid.value}`])

    watchEffect(() => {
        if (params.staleTime) {
            staleTime.value = params.staleTime.value
        }
    })

    return useQuery({
        queryKey: key,
        queryFn: async () => {
            const { data } = await httpClient.get(`v1/ticket-departments/${params.uuid.value}`)

            return ticketDepartmentSchema.parse(data.data)
        },
        enabled: params.enabled,
        staleTime,
    })
}

export const useTicketDepartmentListQuery = (params?: TicketDepartmentListParams) => {
    const enabled = ref(true)
    const page = ref(1)
    const limit = ref(100)
    const sort = ref<Sort[] | undefined>()
    const filter = ref<Filter[] | undefined>()
    const q = ref()
    const view = ref('flat')
    const staleTime = ref(1000 * 60 * 5)
    const key = computed(() => [
        'ticket-departments',
        `q:${q.value}`,
        `page:${page.value}`,
        `limit:${limit.value}`,
        `sort:${JSON.stringify(sort.value)}`,
        `filter:${JSON.stringify(filter.value)}`,
        `view:${view.value}`,
    ])

    watchEffect(() => {
        if (params?.page?.value) {
            page.value = params.page.value
        }

        if (params?.limit?.value) {
            limit.value = params.limit.value
        }

        if (params?.sort?.value) {
            sort.value = params.sort.value
        }

        if (params?.filter?.value) {
            filter.value = params.filter.value
        }

        if (params?.q?.value) {
            q.value = params.q.value
        }

        if (params?.view?.value) {
            view.value = params.view.value
        }

        if (params?.staleTime?.value) {
            staleTime.value = params.staleTime.value
        }

        if (params?.enabled?.value) {
            enabled.value = params.enabled.value
        }
    })

    return useQuery({
        queryKey: key,
        queryFn: async () => {
            const { data } = await httpClient.get('v1/ticket-departments', {
                params: {
                    page: page.value,
                    limit: limit.value,
                    sort: sort.value,
                    filter: filter.value,
                    q: q.value,
                    view: view.value,
                },
            })

            return paginatedTicketDepartmentsSchema.parse(data)
        },
        enabled,
        staleTime,
    })
}

export const useUpdateTicketDepartmentMutation = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (form: TicketDepartmentUpdateForm) => {
            const { data } = await httpClient.patch(`v1/ticket-departments/${form.ticket_uuid}`, {
                title: form.title,
                parent_uuid: form.parent_uuid,
            })

            return logSchema.parse(data.data)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['ticket-departments'],
            })

            toast.success('Ticket department updated successfully.', {
                position: 'top-center',
                closeButton: false,
            })
        },
    })
}

export const useBulkDeleteTicketDepartmentsMutation = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (uuids: string[]) => {
            const { data } = await httpClient.post('v1/ticket-departments/bulk/delete', {
                ticket_uuids: uuids,
            })

            return logsSchema.parse(data.data)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['ticket-departments'],
            })

            toast.success('Ticket departments deleted successfully.', {
                position: 'top-center',
                closeButton: false,
            })
        },
    })
}

export const useBulkArchiveTicketDepartmentsMutation = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (uuids: string[]) => {
            const { data } = await httpClient.post('v1/ticket-departments/bulk/archive', {
                ticket_uuids: uuids,
            })

            return logsSchema.parse(data.data)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['ticket-departments'],
            })

            toast.success('Ticket departments archived successfully.', {
                position: 'top-center',
                closeButton: false,
            })
        },
    })
}

export const useGetTicketDepartmentQuery = (uuid: Ref<string>) => {
    const staleTime = ref(1000 * 60 * 5)
    const key = ref()

    watchEffect(() => {
        key.value = uuid.value
    })

    return useQuery({
        queryKey: [QueryKeys.TicketDepartments, key],
        queryFn: async () => {
            if (!key.value) {
                return null
            }

            const { data } = await httpClient.get(`v1/ticket-departments/${key.value}`)

            return ticketDepartmentSchema.parse(data.data)
        },
        staleTime: staleTime.value,
        select: (data) => {
            return ticketDepartmentSchema.parse(data)
        },
    })
}

export const useCreateTicketDepartmentMutation = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (form: TicketDepartmentUpdateForm) => {
            const { data } = await httpClient.post(`v1/ticket-departments`, form)

            return ticketDepartmentSchema.parse(data.data)
        },
        onSuccess: () => {
            queryClient
                .invalidateQueries({
                    queryKey: [QueryKeys.TicketDepartments],
                })
                .then(() => {})

            toast.success('Ticket Department created successfully.', {
                position: 'bottom-center',
                closeButton: false,
            })
        },
    })
}

export const useEditTicketDepartmentMutation = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (params: { uuid: string; data: TicketDepartmentUpdateForm }) => {
            const { data } = await httpClient.patch(
                `v1/ticket-departments/${params.uuid}`,
                params.data
            )

            return logSchema.parse(data.data)
        },
        onSuccess: (data) => {
            queryClient
                .invalidateQueries({
                    queryKey: [QueryKeys.TicketDepartments],
                })
                .then((r) => {})

            toast.success('Ticket Department updated successfully.', {
                position: 'bottom-center',
                closeButton: false,
            })
        },
    })
}

export const usePaginatedTicketDepartmentQuery = (params?: QueryParams) => {
    const { key, page, limit, sort, filter, q, groupBy } = useListQueryParams(params)

    return useQuery({
        queryKey: [QueryKeys.TicketDepartments, QueryKeys.Paginated, key],
        queryFn: async () => {
            const { data } = await httpClient.get('v1/ticket-departments', {
                params: {
                    page: page.value,
                    limit: limit.value,
                    sort: sort.value,
                    filter: filter.value,
                    q: q.value,
                    group_by: groupBy.value,
                },
            })

            return paginatedTicketDepartmentsSchema.parse(data)
        },
        staleTime: 1000 * 60 * 5,
        placeholderData: keepPreviousData,
    })
}

export const useTicketDepartmentMutations = () => {
    const queryClient = useQueryClient()

    const updateDepartment = useMutation({
        mutationFn: async ({ uuid, departmentUuid }: { uuid: string; departmentUuid: string }) => {
            const result = await httpClient.patch(`/v1/tickets/${uuid}/department`, {
                department_uuid: departmentUuid,
            })
            return result
        },
        onSuccess: async (data, variables) => {
            const departmentData = await queryClient.fetchQuery({
                queryKey: ['ticket-departments', `uuid:${variables.departmentUuid}`],
                queryFn: async () => {
                    const { data } = await httpClient.get(
                        `v1/ticket-departments/${variables.departmentUuid}`
                    )
                    return ticketDepartmentSchema.parse(data.data)
                },
                staleTime: 1000 * 60 * 5,
            })

            await queryClient.setQueryData<Ticket>(
                ['v1', 'tickets', `uuid:${variables.uuid}`],
                (oldData) => {
                    if (!oldData) return oldData

                    return {
                        ...oldData,
                        department: departmentData,
                        type: null,
                    }
                }
            )
        },
        onError: (error: Error) => {
            console.log('Error updating department or clearing type:', error)
        },
    })

    return {
        updateDepartment,
    }
}
